import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Heart, Check, Copy, Plus, HandHeart, Users,
  UtensilsCrossed, Car, Briefcase, Baby, MoreHorizontal, Clock
} from 'lucide-react';

const NEED_CATEGORIES = [
  { key: 'transportation', label: 'Transportation' },
  { key: 'meals', label: 'Meals' },
  { key: 'household', label: 'Managing the house' },
  { key: 'childcare', label: 'Childcare' },
  { key: 'emotional', label: 'Emotional support' },
  { key: 'medications', label: 'Medications' },
  { key: 'work', label: 'Work responsibilities' },
  { key: 'financial', label: 'Financial stress' },
  { key: 'updates', label: 'Updates to family/friends' },
  { key: 'rest', label: 'Rest' },
];

const TASK_CATEGORIES = [
  { key: 'meals', label: 'Meals', icon: UtensilsCrossed },
  { key: 'rides', label: 'Rides', icon: Car },
  { key: 'admin', label: 'Admin', icon: Briefcase },
  { key: 'childcare', label: 'Childcare', icon: Baby },
  { key: 'emotional', label: 'Emotional', icon: Heart },
  { key: 'other', label: 'Other', icon: MoreHorizontal },
];

const CAT_INFO = {
  meals: { icon: UtensilsCrossed, color: 'text-amber-600 bg-amber-50' },
  rides: { icon: Car, color: 'text-blue-600 bg-blue-50' },
  admin: { icon: Briefcase, color: 'text-purple-600 bg-purple-50' },
  childcare: { icon: Baby, color: 'text-pink-600 bg-pink-50' },
  emotional: { icon: Heart, color: 'text-rose-600 bg-rose-50' },
  other: { icon: MoreHorizontal, color: 'text-slate-600 bg-slate-50' },
};

const SupportOnboarding = ({ onComplete, inviteCode: urlInviteCode }) => {
  const { user } = useContext(AuthContext);
  const isSupporter = user?.role === 'care_partner';

  if (isSupporter) {
    return <SupporterOnboarding user={user} onComplete={onComplete} urlInviteCode={urlInviteCode} />;
  }
  return <PatientOnboardingFlow user={user} onComplete={onComplete} />;
};

// ==========================================
// SUPPORTER FLOW: Join circle → See tasks → Done
// ==========================================
const SupporterOnboarding = ({ user, onComplete, urlInviteCode }) => {
  const [screen, setScreen] = useState(urlInviteCode ? 0 : 1);
  const [joinCode, setJoinCode] = useState(urlInviteCode || '');
  const [saving, setSaving] = useState(false);
  const [circleData, setCircleData] = useState(null);
  const [openTasks, setOpenTasks] = useState([]);
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  const firstName = user?.name?.split(' ')[0] || 'friend';

  // Auto-join if code was in the URL (already joined via AuthPage, just fetch the circle)
  useEffect(() => {
    if (urlInviteCode && !autoJoinAttempted) {
      setAutoJoinAttempted(true);
      const doAutoJoin = async () => {
        setSaving(true);
        try {
          // The join was already attempted in AuthPage, but try again to be safe
          const res = await axios.post(`${API}/circles/join`, { invite_code: urlInviteCode.trim().toUpperCase() });
          const circle = res.data.circle;
          if (circle) {
            setCircleData(circle);
            try {
              const todayRes = await axios.get(`${API}/circles/${circle.id}/today`);
              setOpenTasks(todayRes.data.open || []);
            } catch (e) { console.warn('Today snapshot fetch failed (non-blocking):', e?.message); }
            setScreen(2);
          } else {
            setScreen(1);
          }
        } catch (e) {
          // Join failed — show manual entry
          setScreen(1);
        }
        setSaving(false);
      };
      doAutoJoin();
    }
  }, [urlInviteCode, autoJoinAttempted]);

  // Loading state for auto-join
  if (screen === 0) {
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-200 border-t-cyan-600 mx-auto" />
          <p className="text-sm text-muted-foreground">Joining circle...</p>
        </div>
      </div>
    );
  }

  // ===== SCREEN 1: ENTER INVITE CODE =====
  if (screen === 1) {
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="supporter-onboarding-s1">
        <ProgressBar current={1} total={2} />
        <div className="flex-1 flex flex-col justify-center px-8 max-w-md mx-auto w-full">
          <div className="fade-in space-y-6">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Users className="w-7 h-7 text-amber-700 dark:text-amber-400" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome, {firstName}.
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Someone you care about needs support. Enter the invite code they shared with you to join their circle.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Invite code</label>
              <Input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="e.g., ABC12345"
                className="rounded-xl text-center text-lg h-14 font-mono tracking-[0.15em]"
                data-testid="supporter-invite-code"
                autoFocus
              />
            </div>
          </div>
        </div>
        <div className="px-8 pb-10 max-w-md mx-auto w-full space-y-2">
          <Button
            onClick={async () => {
              if (!joinCode.trim()) { toast.error('Enter the invite code'); return; }
              setSaving(true);
              try {
                const res = await axios.post(`${API}/circles/join`, { invite_code: joinCode.trim() });
                const circle = res.data.circle;
                setCircleData(circle);
                // Fetch open tasks for this circle
                try {
                  const todayRes = await axios.get(`${API}/circles/${circle.id}/today`);
                  setOpenTasks(todayRes.data.open || []);
                } catch (e) { console.warn('Could not fetch tasks for circle:', e.message); }
                setScreen(2);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Invalid invite code. Check and try again.');
              }
              setSaving(false);
            }}
            className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white"
            disabled={saving || !joinCode.trim()}
            data-testid="supporter-join-btn"
          >
            {saving ? 'Joining...' : <>Join Circle <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
          <button
            onClick={() => {
              localStorage.setItem('hasSeenOnboarding', 'true');
              onComplete();
            }}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 text-center"
            data-testid="supporter-skip"
          >
            I don't have a code yet — skip for now
          </button>
        </div>
      </div>
    );
  }

  // ===== SCREEN 2: YOU'RE IN + OPEN TASKS =====
  if (screen === 2) {
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="supporter-onboarding-s2">
        <ProgressBar current={2} total={2} onBack={() => setScreen(1)} />
        <div className="flex-1 overflow-y-auto px-8 max-w-md mx-auto w-full py-6">
          <div className="slide-up space-y-6">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                You're in {circleData?.name || 'the circle'}!
              </h2>
              <p className="text-sm text-muted-foreground">
                Here's how you can help — claim what you can do, no pressure.
              </p>
            </div>

            {openTasks.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Needs help ({openTasks.length})
                </p>
                {openTasks.slice(0, 5).map(task => {
                  const cat = CAT_INFO[task.category] || CAT_INFO.other;
                  const CatIcon = cat.icon;
                  return (
                    <div key={task.id} className="surface-card rounded-xl p-4" data-testid={`supporter-task-${task.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg ${cat.color} flex items-center justify-center flex-shrink-0`}>
                          <CatIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{task.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{task.creator_name}</p>
                          {task.due_date && (
                            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {openTasks.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center">+ {openTasks.length - 5} more tasks</p>
                )}
              </div>
            ) : (
              <div className="surface-card rounded-xl p-6 text-center">
                <HandHeart className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No open tasks yet.</p>
                <p className="text-xs text-muted-foreground/70 mt-1">When they post a task, you'll see it here and can claim it.</p>
              </div>
            )}

            <div className="surface-card rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-foreground">How this works:</p>
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <p className="flex items-start gap-2"><span className="text-cyan-500 font-bold">1.</span> Tasks appear when the patient posts a need</p>
                <p className="flex items-start gap-2"><span className="text-cyan-500 font-bold">2.</span> Tap "I'll do this" to claim what you can handle</p>
                <p className="flex items-start gap-2"><span className="text-cyan-500 font-bold">3.</span> Mark it done when you've completed it</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-8 pb-10 max-w-md mx-auto w-full">
          <Button
            onClick={() => {
              localStorage.setItem('hasSeenOnboarding', 'true');
              onComplete();
            }}
            className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white"
            data-testid="supporter-finish"
          >
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

// ==========================================
// PATIENT FLOW: Name circle → Invite code → Needs → First task
// ==========================================
const PatientOnboardingFlow = ({ user, onComplete }) => {
  const [screen, setScreen] = useState(1);
  const [saving, setSaving] = useState(false);

  const [circleName, setCircleName] = useState(`${user?.name?.split(' ')[0] || 'My'}'s Care Circle`);
  const [circleData, setCircleData] = useState(null);
  const [selectedNeeds, setSelectedNeeds] = useState({});
  const [taskTitle, setTaskTitle] = useState('');
  const [taskCategory, setTaskCategory] = useState('other');

  const firstName = user?.name?.split(' ')[0] || 'friend';
  const goTo = (s) => setScreen(s);

  const toggleNeed = (key) => {
    setSelectedNeeds(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  // ===== SCREEN 1: NAME YOUR CIRCLE =====
  if (screen === 1) {
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="onboarding-s1">
        <ProgressBar current={1} total={4} />
        <div className="flex-1 flex flex-col justify-center px-8 max-w-md mx-auto w-full">
          <div className="fade-in space-y-6">
            <div className="flex justify-center">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border border-cyan-300/40" />
                <div className="absolute inset-2 rounded-full border border-cyan-400/60" />
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
                  <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
                </div>
              </div>
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome, {firstName}.
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Let's create your Care Circle — a private space where the people around you can see what's needed and help.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Name your circle</label>
              <Input
                value={circleName}
                onChange={(e) => setCircleName(e.target.value)}
                placeholder="e.g., Jordan's Care Circle"
                className="rounded-xl text-center text-base h-12"
                data-testid="onboarding-circle-name"
                autoFocus
              />
            </div>
          </div>
        </div>
        <div className="px-8 pb-10 max-w-md mx-auto w-full">
          <Button
            onClick={async () => {
              if (!circleName.trim()) { toast.error('Give your circle a name'); return; }
              setSaving(true);
              try {
                const res = await axios.post(`${API}/circles`, { name: circleName.trim() });
                setCircleData(res.data);
                goTo(2);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Could not create circle');
              }
              setSaving(false);
            }}
            className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white"
            disabled={saving || !circleName.trim()}
            data-testid="onboarding-create-circle"
          >
            {saving ? 'Creating...' : <>Create Circle <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  }

  // ===== SCREEN 2: INVITE CODE =====
  if (screen === 2) {
    const inviteCode = circleData?.invite_code || '';
    const shareMessage = `Hi! ${firstName} has invited you to be in their ${circleData?.name || 'care circle'}. They are using an app called CircleUpSquad to coordinate their helpers. Sign up with this link and you'll be automatically added:\n\nhttps://circleupsquad.com/auth?code=${inviteCode}`;

    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="onboarding-s2">
        <ProgressBar current={2} total={4} onBack={() => goTo(1)} />
        <div className="flex-1 flex flex-col justify-center px-8 max-w-md mx-auto w-full">
          <div className="slide-up space-y-6">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {circleData?.name || 'Your circle'} is ready!
              </h2>
              <p className="text-sm text-muted-foreground">Copy this message and send it to the people you want in your circle.</p>
            </div>
            <div className="surface-card rounded-2xl p-4 space-y-3">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{shareMessage}</p>
              <button onClick={() => { navigator.clipboard.writeText(shareMessage).then(() => toast.success('Invite message copied — send it to your people!')).catch(() => toast.error('Could not copy — try long-pressing to copy manually')); }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 text-sm text-cyan-700 dark:text-cyan-300 font-medium hover:bg-cyan-100 transition-colors" data-testid="onboarding-copy-message">
                <Copy className="w-4 h-4" /> Copy invite message
              </button>
            </div>
            <div className="surface-card rounded-2xl p-4 text-center space-y-1">
              <p className="text-xs text-muted-foreground">Your invite code</p>
              <p className="text-2xl font-mono font-bold text-foreground tracking-[0.2em]">{inviteCode}</p>
            </div>
          </div>
        </div>
        <div className="px-8 pb-10 max-w-md mx-auto w-full">
          <Button onClick={() => goTo(3)} className="w-full h-11 rounded-xl btn-primary" data-testid="onboarding-s2-next">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ===== SCREEN 3: WHAT FEELS HARD =====
  if (screen === 3) {
    const selectedKeys = Object.keys(selectedNeeds);
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="onboarding-s3">
        <ProgressBar current={3} total={4} onBack={() => goTo(2)} />
        <div className="flex-1 overflow-y-auto px-8 max-w-md mx-auto w-full py-6">
          <div className="slide-up space-y-5">
            <div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>What feels hardest right now?</h2>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">This helps your circle understand where support is most needed.</p>
            </div>
            <div className="space-y-2">
              {NEED_CATEGORIES.map(c => (
                <button key={c.key} onClick={() => toggleNeed(c.key)}
                  className={`w-full p-3.5 rounded-xl border text-sm text-left transition-colors flex items-center gap-3 ${
                    selectedNeeds[c.key] ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`} data-testid={`onboarding-need-${c.key}`}>
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    selectedNeeds[c.key] ? 'bg-cyan-600 border-cyan-600' : 'border-slate-300 dark:border-slate-600'
                  }`}>{selectedNeeds[c.key] && <Check className="w-3 h-3 text-white" />}</div>
                  <span className={selectedNeeds[c.key] ? 'text-cyan-700 dark:text-cyan-300 font-medium' : 'text-foreground'}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-8 pb-8 max-w-md mx-auto w-full space-y-2">
          <p className="text-xs text-muted-foreground text-center">{selectedKeys.length} selected</p>
          <Button onClick={async () => {
            try { await axios.post(`${API}/onboarding/needs`, { needs: selectedKeys.map(k => ({ category: k, score: 5, selected: true })) }); } catch (e) { console.warn('Onboarding needs save skipped:', e?.message); }
            goTo(4);
          }} className="w-full h-11 rounded-xl btn-primary" data-testid="onboarding-s3-next">
            {selectedKeys.length > 0 ? 'Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ===== SCREEN 4: FIRST TASK =====
  if (screen === 4) {
    return (
      <div className="fixed inset-0 z-[60] bg-white dark:bg-slate-950 flex flex-col" data-testid="onboarding-s4">
        <ProgressBar current={4} total={4} onBack={() => goTo(3)} />
        <div className="flex-1 flex flex-col justify-center px-8 max-w-md mx-auto w-full">
          <div className="slide-up space-y-6">
            <div>
              <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>What do you need help with today?</h2>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Post your first task so your circle can see it and claim it.</p>
            </div>
            <div className="space-y-4">
              <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="e.g., Ride to appointment Thursday" className="rounded-xl h-12 text-base" data-testid="onboarding-task-title" autoFocus />
              <div className="flex flex-wrap gap-2">
                {TASK_CATEGORIES.map(c => (
                  <button key={c.key} onClick={() => setTaskCategory(c.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      taskCategory === c.key ? 'bg-cyan-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-foreground hover:bg-slate-200'
                    }`} data-testid={`onboarding-cat-${c.key}`}>
                    <c.icon className="w-3 h-3" /> {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="px-8 pb-10 max-w-md mx-auto w-full">
          <Button onClick={async () => {
            setSaving(true);
            if (taskTitle.trim() && circleData?.id) {
              try { await axios.post(`${API}/circles/${circleData.id}/tasks`, { title: taskTitle.trim(), category: taskCategory }); toast.success('First task posted!'); } catch (e) { console.warn('Task creation skipped:', e.message); }
            }
            localStorage.setItem('hasSeenOnboarding', 'true');
            setSaving(false);
            onComplete();
          }} className="w-full h-[52px] rounded-xl text-base font-semibold bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white" disabled={saving} data-testid="onboarding-finish">
            {saving ? 'Saving...' : taskTitle.trim() ? <>Post & Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" /></> : <>Skip — Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

// ==========================================
// PROGRESS BAR
// ==========================================
const ProgressBar = ({ current, total, onBack }) => (
  <div className="px-6 pt-4 pb-2">
    <div className="flex items-center gap-3">
      {onBack && (
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="onboarding-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}
      <div className="flex-1 flex gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <div key={`bar-${i}`} className={`h-1 flex-1 rounded-full transition-all duration-500 ${i < current ? 'bg-cyan-500' : 'bg-slate-200 dark:bg-slate-800'}`} />
        ))}
      </div>
      <span className="text-[11px] text-muted-foreground">{current}/{total}</span>
    </div>
  </div>
);

export default SupportOnboarding;
